import { AppBox } from '../../../../component/common/analog/molecule/AppBox'
import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Box, Fade, Pagination, Typography, useMediaQuery } from '@mui/material'
import { useELearningStore } from '../../store/e-learning.store'
import { AppProgressLine } from 'src/component/common/analog/molecule/loader/AppProgressLine'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { CourseListPresentationItem } from './CourseListPresentationItem'
import { ELearningCourseCategory } from '../../model/ELearningCourseCategory'
import { AppCarousel } from 'src/component/common/analog/molecule/carousel/AppCarousel'
import { CourseItem } from './CourseItem'
import { CourseSearch } from './CourseSearch'

export type CourseListProps = {
  categoryKey: string
  categoryTitle: string
}

export const CourseList = observer((props: CourseListProps) => {
  const eLearningStore = useELearningStore()
  const { translation } = useLocalizationStore()
  const [searchTerm, setSearchTerm] = useState('')
  const { isLoading: isLoadingCoursesByCategory } = eLearningStore.getGenericFieldsState('getCoursesByCategory')
  const { isLoading: isLoadingAllCourses } = eLearningStore.getGenericFieldsState('getAllCourses')
  const isSmallScreen = useMediaQuery('(max-width:900px)')

  const texts = translation.pages.eLearning
  const isLoading = isLoadingCoursesByCategory || isLoadingAllCourses
  const [selectedSubCategory, setSelectedSubCategory] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 30

  useEffect(() => {
    eLearningStore.loadSubCategoriesIfNeeded()
    eLearningStore.clearSearch()
    eLearningStore.selectedFilters = undefined
    setSelectedFilterValues('all')
    eLearningStore.courseSubCategory = []
    eLearningStore.getCourseSubCategory(props.categoryKey)
    eLearningStore.getAllCourses(props.categoryKey, 0, pageSize)
  }, [])

  const setSelectedFilterValues = (type: string) => {
    const categoryId = eLearningStore.categories?.find((category: ELearningCourseCategory) => category.titleLocale === props.categoryKey)?.id
    const filteredSubs = eLearningStore.categoriesWithSubs?.filter((subCategory: any) => subCategory.category.id === categoryId).map((subCategory: any) => subCategory.subCategory)
    
    if (type === 'all') {
      eLearningStore.selectedFilters = filteredSubs
    } else {
      const selectedSub = filteredSubs.find((subCategory: any) => subCategory.id === type)
      eLearningStore.selectedFilters = [selectedSub]
    }
  }

  const transform = (category: ELearningCourseCategory, index: number) => {
    return <CourseItem key={category.id} item={category} index={index} selectedSubCategory={selectedSubCategory} clickHandler={getCourses} />
  }

  const getCourses = async (subCategory: ELearningCourseCategory) => {
    eLearningStore.clearSearch()
    eLearningStore.selectedFilters = undefined

    if (searchTerm.length > 0) {
      if (selectedSubCategory === subCategory.id) {
        setSelectedSubCategory('')
        setSelectedFilterValues('all')
      } else {
        setSelectedSubCategory(subCategory.id)
        setSelectedFilterValues(subCategory.id)
      }
      await eLearningStore.search(searchTerm, 0, pageSize)
      
      return
    }
  
    if (selectedSubCategory === subCategory.id) {
      setSelectedSubCategory('')
      setSelectedFilterValues('all')
      await eLearningStore.getAllCourses(props.categoryKey, 0, pageSize)
    } else {
      setSelectedSubCategory(subCategory.id)
      setSelectedFilterValues(subCategory.id)
      await eLearningStore.getCoursesByCategory(subCategory.id, 0, pageSize)
    }
  }

  const extendResults = async (newPageLength: number) => {
    if (eLearningStore.courseList && eLearningStore.courseList.length !== eLearningStore.courseCount) {
      if (searchTerm.length > 0) {
        await eLearningStore.search(searchTerm, newPageLength, pageSize)
      } else if (selectedSubCategory) {
        await eLearningStore.getCoursesByCategory(selectedSubCategory, newPageLength, pageSize)
      } else {
        await eLearningStore.getAllCourses(props.categoryKey, newPageLength, pageSize)
      }
    }
  }

  const setSearchTermHandler = async (term: string) => {
    setSearchTerm(term)
    eLearningStore.clearSearch()
    if (term === '') {
      setSelectedSubCategory('')
      setSelectedFilterValues('all')
      await eLearningStore.getAllCourses(props.categoryKey, 0, pageSize)
    } else {
      await eLearningStore.search(term, 0, pageSize)
    }
  }

  return (
    <AppBox sx={styles.root}>
      <CourseSearch categoryKey={props.categoryKey} setSearchTerm={(term) => {
        setCurrentPage(1)
        setSearchTermHandler(term)
      }} />
      {!isSmallScreen && <Box sx={styles.chipContainer}>
        {eLearningStore.courseSubCategory && eLearningStore.courseSubCategory?.length > 1 && eLearningStore.courseSubCategory.map(transform)}
      </Box>}
      {isSmallScreen && <AppCarousel showProgressBar={false}>
        {eLearningStore.courseSubCategory && eLearningStore.courseSubCategory?.length > 1 && eLearningStore.courseSubCategory.map(transform)}
      </AppCarousel>
      }
      <AppProgressLine isShowing={isLoading} />
      {!isLoading && eLearningStore.courseList && eLearningStore.courseList.length === 0 && <Caption sx={{ color: 'text.secondary' }} textValue={texts.missingCourse} />}
      {!isLoading && eLearningStore.courseList && eLearningStore.courseList.length > 0 && (
        <Fade in={eLearningStore.courseList?.length > 0} mountOnEnter unmountOnExit>
          <AppBox sx={styles.courseList}>
            {eLearningStore.courseList.map(course => (
              <CourseListPresentationItem key={course.id} item={course} />
            ))}
          </AppBox>
        </Fade>
      )}
      {!isLoading && eLearningStore.courseList && eLearningStore.courseList.length > 0 && eLearningStore.courseList.length !== eLearningStore.courseCount && (
        <AppBox alignItems='center' justifyContent='center' width='100%' gap={5}>
          <Typography fontSize={13} fontWeight={500} color={'secondary.main'}>{`${eLearningStore.courseCount} ${texts.courses().toLowerCase()}`}</Typography>
          <Pagination
            shape='rounded'
            color='primary'
            count={Math.ceil((eLearningStore.courseCount || 0) / pageSize)}
            page={currentPage}
            showFirstButton
            showLastButton
            onChange={(_, page) => {
              const newPageLength = (page - 1) * pageSize
              setCurrentPage(page)
              extendResults(newPageLength)
            }}
            sx={{ '& .MuiPaginationItem-root': { fontSize: 12, height: 22 } }}
          />
        </AppBox>
      )}
    </AppBox>
  )
})

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
    padding: 20,
  },
  chipContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 5,
    flexWrap: 'wrap',
  },
  courseList: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: { xs: 'center', lg: 'flex-start' },
    gap: 10,
  },
}
